import React from 'react';
import { Paper, Select, MenuItem, FormControl, InputLabel, Box, IconButton, Tooltip, useTheme, Typography, Chip } from '@mui/material';
import { IosShare, DashboardCustomize, PushPin, QueryStats } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import { TemplateSearchDialog } from 'components/widgets/utility/TemplateSearch/TemplateSearchDialog';
import { findFirstNodeWithField } from 'system/query/QueryUtility';
import { QueryLeafNode } from 'system/query/QueryLeafNode';
import { AutoTimestampDisplay } from 'components/formatting/AutoTimestampDisplay';
import { ResourceActionDialog } from 'components/adminResources/ResourceActionDialog';
import { useDashboard } from 'routes/dashboard/Dashboard';
import { enqueueSnackbar } from 'notistack';
import { permissionTest } from 'system/InfegyUser';
import TruncatedString from 'components/formatting/TruncatedString';
import ConvertDashboardToReportDialog from './ConvertDashboardToReportDialog';
import { getDashboardNavigatePath, parsePageParams } from 'components/resources/dashboards/DashboardNavigationUtils';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTemplateQueries } from 'routes/dashboard/DashboardPageRoot';
import DashboardTemplateQuerySelectorDialog from 'components/resources/dashboards/DashboardTemplateQuerySelectorDialog';
import WidgetMultiqueryDialog from 'components/widgetParts/WidgetMultiqueryDialog';
import { Widget, WidgetQuery } from 'system/widget/Widget';
import { APIError, enqueueAPIErrorSnackbar } from 'system/APIRequest';

export interface TemplateQuerySelectorProps {
  showHighlights?: boolean;

  pageWidgets?: Array<Widget>;

  isPinned?: boolean;
  setIsPinned?: (isPinned: boolean) => void;

  onRefreshWidgets: () => void;
  onUpdateWidget: (widgetId: string, updatedWidgetInfo: any, resetPage?: boolean) => Promise<any>;
}

export default function TemplateQuerySelector(props: TemplateQuerySelectorProps): JSX.Element {
  const { i18n } = useTranslation();
  const dashboard = useDashboard();
  const theme = useTheme();
  const navigate = useNavigate();

  const { pageWidgets, onUpdateWidget } = props;

  const { allTemplateQueries, selectedTemplateQueries, globalQuery } = useTemplateQueries();

  const [queryDialogIsOpen, setQueryDialogIsOpen] = React.useState(false);
  const [bulkWidgetQueryDialogIsOpen, setBulkWidgetQueryDialogIsOpen] = React.useState(false);

  const editIconStyle = { display:"inline-block", opacity: 0.5, ml:1, "&:hover": { opacity: 1 }, color: props.showHighlights ? theme.palette.primary.main : theme.palette.text.primary };

  const [globalStartDate, setGlobalStartDate] = React.useState<string | undefined>(undefined);
  const [globalEndDate, setGlobalEndDate] = React.useState<string | undefined>(undefined);

  const [shareDashboardDialogIsOpen, setShareDashboardDialogIsOpen] = React.useState(false);
  const [duplicateDashboardDialogIsOpen, setDuplicateDashboardDialogIsOpen] = React.useState(false);

  const [createReportDialogIsOpen, setCreateReportDialogIsOpen] = React.useState(false);

  const [customSelectionDialogIsOpen, setCustomSelectionDialogIsOpen] = React.useState(false);

  let selectedTemplateQueryIndex = undefined;
  let selectedTemplateQuery = undefined;
  if (selectedTemplateQueries?.length === 1 && allTemplateQueries?.length > 0) {
    selectedTemplateQuery = selectedTemplateQueries[0];
    selectedTemplateQueryIndex = allTemplateQueries.findIndex((query) => query.id === selectedTemplateQueries[0].id);
  }

  const allIsSelected = selectedTemplateQueries !== undefined && allTemplateQueries !== undefined &&
        selectedTemplateQueries.length === allTemplateQueries.length && selectedTemplateQueries.length > 1;
  const customIsSelected = selectedTemplateQueries !== undefined && allTemplateQueries !== undefined &&
        selectedTemplateQueries.length < allTemplateQueries.length && selectedTemplateQueries.length > 1;

  React.useEffect(() => {
    if (globalQuery?.query) {
      const dateSegment = findFirstNodeWithField(globalQuery.query, "published") as QueryLeafNode;
      if (dateSegment && dateSegment.value) {
        setGlobalStartDate(dateSegment.value as string);
      } else if (dateSegment && dateSegment.upper && dateSegment.lower) {
        setGlobalStartDate(dateSegment.lower as string);
        setGlobalEndDate(dateSegment.upper as string);
      }
    } else {
      setGlobalStartDate(undefined);
      setGlobalEndDate(undefined);
    }
  }, [globalQuery]);

  async function bulkSaveWidgetQueries(widgetQueries: WidgetQuery[]) {
    if (!pageWidgets || pageWidgets.length === 0 || !onUpdateWidget) {
      return;
    }

    const updatePromises = pageWidgets.map(async (widget) => {
      const queriesToSubmit = widgetQueries?.length ? widgetQueries : undefined;
      const requestInfo = { ...widget.request_info };
      requestInfo.queries = queriesToSubmit;
      const updatedWidgetInfo = { request_info: requestInfo, cache: {} };
      return await onUpdateWidget(widget.id, updatedWidgetInfo, false);
    });

    await Promise.all(updatePromises);
  }

  const border = props.showHighlights ? `2px solid ${theme.palette.primary.main}` : `none`;
  return (
    <Paper sx={{ p: 1, mb: 0, ml: 0, mr: 0, border:border  }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: "1fr", md: "auto auto", alignItems: "center" }}}>
      <Box sx={{ textAlign: 'left', pt: 0.5 }}>
        <InputLabel sx={{ mr: 0, ml: 1, display: "flex", alignItems: "center", flexWrap: "wrap" }}>
        {allTemplateQueries && allTemplateQueries.length > 1 &&
          <>
            {i18n.t("dashboard.active-template-queries")}
            <FormControl size="small" variant="standard" sx={{ minWidth: 120, ml:1, mr: 0 }}>
              <Select
                variant='outlined'
                value={allIsSelected ? "all" : (customIsSelected ? "custom" : selectedTemplateQuery?.id || "")}
                onChange={(event) => {
                  // Keep the selection box from staying highlighted after closing
                  setTimeout(() => {
                    (document?.activeElement as HTMLElement)?.blur();
                  }, 0);

                  const templateQueryId = event.target.value;
                  if (templateQueryId === "custom") {
                    setCustomSelectionDialogIsOpen(true);
                    return;
                  }

                  const pathParts = parsePageParams();
                  pathParts.templateQueryIndices = [templateQueryId];
                  if (templateQueryId !== "all") {
                    if (allTemplateQueries.length > 0) {
                      pathParts.templateQueryIndices = [allTemplateQueries.findIndex((query) => query.id === templateQueryId)];
                    } else {
                      pathParts.templateQueryIndices = [0];
                    }
                  }
                  const navigatePath = getDashboardNavigatePath(pathParts);
                  navigate(navigatePath, { replace: true });
                }}
                onClose={() => {
                  // Keep the selection box from staying highlighted after closing
                  setTimeout(() => {
                    (document?.activeElement as HTMLElement)?.blur();
                  }, 0);
                }}
              >
                {allTemplateQueries.map((tq, index) => (
                  <MenuItem key={tq.id} value={tq.id} divider={index === allTemplateQueries.length - 1} sx={{ borderRadius: 0 }}>
                    <span style={{ color: tq.color, marginRight: '4px' }}>● </span>{tq.query.name}
                  </MenuItem>
                ))}
                <MenuItem key={"all"} value={"all"}>
                  {i18n.t("dashboard.all-template-queries")}
                </MenuItem>
                <MenuItem key={"custom"} value={"custom"}>
                  {i18n.t("custom")}
                </MenuItem>
              </Select>
            </FormControl>
          </>
        }
        {allTemplateQueries && allTemplateQueries.length === 1 && !allIsSelected &&
          <>
            <Typography sx={{ mr: 2 }}>{i18n.t("dashboard.active-template-query")}:</Typography>
            <Box sx={{ display: 'inline', fontWeight: 'bold', color: allTemplateQueries[0]?.query?.meta?.color || ((dashboard?._modifiable || dashboard?.template_type) ? theme.palette.primary.main : theme.palette.text.secondary) }}>
              <TruncatedString text={allTemplateQueries[0]?.query.name} maxLength={40} />
            </Box>
          </>
        }
        {(!allTemplateQueries || !allTemplateQueries.length) &&
          <Box sx={{ display: 'inline', fontWeight: 'bold', color: props.showHighlights ? theme.palette.primary.main : theme.palette.text.primary }}>
            {i18n.t("dashboard.no-active-template-query")}
          </Box>
        }
        {(dashboard?._modifiable || dashboard?.template_type) &&
          <IconButton size="small" sx={editIconStyle}
            onClick={() => setQueryDialogIsOpen(true)}>
            <EditIcon />
          </IconButton>
        }
        </InputLabel>
        {customIsSelected &&
          <Box sx={{ ml: 1, mt: 0.5 }}>
            {selectedTemplateQueries.map((tq, index) => (
              <Chip
                sx={{ml: 1,
                  backgroundColor: `color(from ${tq.color} srgb r g b / 0.6)`,
                  fontWeight: 'bold',
                  '&:hover': { backgroundColor: tq.color },
                 }}
                key={index}
                label={tq.query.name}
                onClick={() => {
                  setCustomSelectionDialogIsOpen(true);
                }}
              />
            ))}
          </Box>
        }
      </Box>
      <Box sx={{ textAlign: { xs: 'left', md: 'right' }, ml: { xs: 2, md: 0}, whiteSpace: "nowrap"}}>
        {globalStartDate &&
          <InputLabel sx={{display:"inline-block", pr: 1, ml: 0, mr: 2, verticalAlign:"middle", borderRight: `1px solid ${theme.palette.divider}`}} onClick={() => setQueryDialogIsOpen(true)}>
            <Box sx={{display:"inline-block", ml: 0, mr: 0.5 }}>
              <AutoTimestampDisplay value={globalStartDate} />
            </Box>
            {i18n.t("to")}
            <Box sx={{display:"inline-block", ml: 0.5, mr: 0 }}>
              <AutoTimestampDisplay value={globalEndDate || ""} blankIsNow={true} />
            </Box>
            <IconButton size="small" sx={editIconStyle}
              onClick={() => setQueryDialogIsOpen(true)}>
              <EditIcon />
            </IconButton>
          </InputLabel>
        }
        <IconButton
          sx={{ cursor: "pointer", display:"inline-block" , ml: -1, mr: 0, verticalAlign:"middle", opacity: props.isPinned ? 1 : 0.5, "&:hover": { opacity: 1 } }}
          color={props.isPinned ? "primary" : "default"}
          onClick={() => props.setIsPinned && props.setIsPinned(!props.isPinned)}
        >
          <PushPin />
        </IconButton>
        {(dashboard?._owned_by_you && dashboard?._modifiable) &&
          <IconButton
            sx={{ cursor: "pointer", display:"inline-block" , ml: -1, mr: 0, verticalAlign:"middle" }}
            aria-label={i18n.t("dashboard.share-dashboard")}
            onClick={(e) => {
              e.preventDefault();
              setShareDashboardDialogIsOpen(true);
            }}>
            <IosShare />
          </IconButton>
        }
        {(dashboard?._modifiable) &&
          <Tooltip title={i18n.t("dashboard.bulk-edit-widget-queries")} followCursor>
            <IconButton
              sx={{ cursor: "pointer", display:"inline-block" , ml: 0, mr: 1, verticalAlign:"middle" }}
              aria-label={i18n.t("dashboard.bulk-edit-widget-queries")}
              onClick={(e) => {
                e.preventDefault();
                setBulkWidgetQueryDialogIsOpen(true);
              }}>
              <Box component="span" sx={{ fontSize: '1.5rem', lineHeight: 0 }}>
                <QueryStats />
              </Box>
            </IconButton>
          </Tooltip>
        }
        {(permissionTest("create_dashboard") && allTemplateQueries && allTemplateQueries.length > 0) &&
          <Tooltip title={i18n.t("dashboard.convert-template-to-report-dialog.dialog-title")} followCursor>
            <IconButton
              sx={{ cursor: "pointer", display:"inline-block" , ml: 0, mr: 1, verticalAlign:"middle" }}
              aria-label={i18n.t("dashboard.convert-template-to-report-dialog.dialog-title")}
              onClick={(e) => {
                e.preventDefault();
                setCreateReportDialogIsOpen(true);
              }}>
              <DashboardCustomize />
            </IconButton>
          </Tooltip>
        }
      </Box>

      {queryDialogIsOpen &&
        <TemplateSearchDialog
          onClose={() => setQueryDialogIsOpen(false)}
          onSubmit={(queryIds: string[]) => {
              const pathParts = parsePageParams();
              pathParts.queryIds = queryIds.join("-");
              if (globalQuery) {
                pathParts.queryIds = `${pathParts.queryIds}-g_${globalQuery.id}`;
              }
              pathParts.templateQueryIndices = [0];
              const navigatePath = getDashboardNavigatePath(pathParts);
              navigate(navigatePath, { replace: true });
          }}
        />
      }
      {shareDashboardDialogIsOpen &&
        <ResourceActionDialog
          open={shareDashboardDialogIsOpen}
          accountAccess={dashboard.access}
          action="share"
          resourceIds={dashboard.id}
          endpoint="dashboard"
          onClose={() => setShareDashboardDialogIsOpen(false) }
          handleComplete={(wasSuccessful: boolean) => {
            if (wasSuccessful) {
              setShareDashboardDialogIsOpen(false);
              enqueueSnackbar(i18n.t("resource-editor.save-successful"), { variant: "success" });
              window.location.reload();
            }
        }} />
      }
      {duplicateDashboardDialogIsOpen &&
        <ResourceActionDialog
          open={duplicateDashboardDialogIsOpen}
          asAdmin={false}
          reassignAccount={false}
          reassignable={false}
          accountAccess={dashboard.access}
          action="duplicate"
          resourceIds={dashboard.id}
          endpoint="dashboard"
          onClose={() => setDuplicateDashboardDialogIsOpen(false) }
          handleComplete={(wasSuccessful: boolean) => {
            if (wasSuccessful) {
              setDuplicateDashboardDialogIsOpen(false);
              enqueueSnackbar(i18n.t("resource-editor.duplicate-successful"), { variant: "success" });
            }
        }} />
      }
      {createReportDialogIsOpen &&
        <ConvertDashboardToReportDialog
          dashboard={dashboard}
          templateQueries={allTemplateQueries}
          selectedTemplateQueryIndex={selectedTemplateQueryIndex || 0}
          onClose={() => setCreateReportDialogIsOpen(false) }
         />
      }
      {customSelectionDialogIsOpen &&
        <DashboardTemplateQuerySelectorDialog
          allTemplateQueries={allTemplateQueries}
          selectedTemplateQueries={selectedTemplateQueries}
          onClose={() => setCustomSelectionDialogIsOpen(false)}
        />
      }
      {bulkWidgetQueryDialogIsOpen &&
        <WidgetMultiqueryDialog
          dashboardDatasetId={dashboard?.template_type ? (dashboard?.dataset_id || undefined) : undefined}
          queryIds={[]}
          onClose={() => {
            setBulkWidgetQueryDialogIsOpen(false);
          }}
          onSubmit={(widgetQueries: WidgetQuery[]) => {
            bulkSaveWidgetQueries(widgetQueries).then(() => {
              setTimeout(() => {
                window.location.reload();
              }, 500);
            }).catch((error: APIError) => {
              enqueueAPIErrorSnackbar(error, enqueueSnackbar);
            });
            setBulkWidgetQueryDialogIsOpen(false);
          }}
        />
      }
      </Box>
    </Paper>
  );
}
